import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/layout/PageHeader';
import { staffCardClass } from '../components/layout/staffPageStyles';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  BarChart3, Wrench, Users, DollarSign, Clock, TrendingUp,
  Package, AlertTriangle, CheckCircle, Activity,
} from 'lucide-react';

// Couleurs indexées sur les valeurs de statut renvoyées par le backend (NE PAS traduire ces clés).
const STATUS_COLORS = {
  'En attente': '#f59e0b', 'En cours': '#3b82f6', 'Terminé': '#10b981',
  'Livré': '#8b5cf6', 'Annulé': '#ef4444',
};

// Mapping séparé valeur backend -> clé i18n pour l'affichage (repli sur la valeur brute).
const STATUS_LABEL_KEYS = {
  'En attente': 'companyAnalytics.statusPending',
  'En cours': 'companyAnalytics.statusInProgress',
  'Terminé': 'companyAnalytics.statusCompleted',
  'Livré': 'companyAnalytics.statusDelivered',
  'Annulé': 'companyAnalytics.statusCancelled',
};

const card = staffCardClass;

const CompanyAnalyticsPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/overview')
      .then(r => setData(r.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [api]);

  const body = loading ? (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-2 border-[var(--theme-primary)] border-t-transparent rounded-full animate-spin" />
    </div>
  ) : !data ? (
    <p className="text-gray-500 text-center py-10">{t('companyAnalytics.loadError')}</p>
  ) : (
    <AnalyticsContent data={data} />
  );

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="company-analytics">
        <PageHeader
          title={t('companyAnalytics.title')}
          subtitle={t('companyAnalytics.subtitle')}
          backTo="/dashboard"
          icon={BarChart3}
          testId="analytics-page-header"
        />
        {body}
      </div>
    </Layout>
  );
};

function AnalyticsContent({ data }) {
  const { t, formatCurrency } = useLanguage();
  const rep = data.repairs || {};
  const rev = data.revenue || {};
  const cli = data.clients || {};
  const stk = data.stock || {};
  const dist = data.status_distribution || [];
  const trend = data.daily_trend || [];
  const maxTrend = Math.max(...trend.map(t => t.count), 1);

  // Montant en euros sans décimale (préserve l'ancien comportement .toFixed(0)).
  const eur0 = (n) => formatCurrency(n || 0, 'EUR', { maximumFractionDigits: 0 });

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Wrench, label: t('companyAnalytics.kpiRepairsToday'), value: rep.today || 0, sub: t('companyAnalytics.kpiThisWeek', { count: rep.week || 0 }), color: 'bg-[rgba(var(--theme-primary-rgb),0.15)] text-[var(--theme-primary)]' },
          { icon: DollarSign, label: t('companyAnalytics.kpiRevenueToday'), value: eur0(rev.today), sub: t('companyAnalytics.kpiThisMonthAmount', { amount: eur0(rev.month) }), color: 'bg-emerald-500/15 text-emerald-400' },
          { icon: Users, label: t('companyAnalytics.kpiClients'), value: cli.total || 0, sub: t('companyAnalytics.kpiNewThisMonth', { count: cli.new_month || 0 }), color: 'bg-blue-500/15 text-blue-400' },
          { icon: Clock, label: t('companyAnalytics.kpiAvgTime'), value: rep.avg_days > 0 ? t('companyAnalytics.daysShort', { count: rep.avg_days }) : '-', sub: t('companyAnalytics.kpiPerRepair'), color: rep.avg_days <= 3 ? 'bg-emerald-500/15 text-emerald-400' : rep.avg_days <= 7 ? 'bg-amber-500/15 text-amber-400' : 'bg-red-500/15 text-red-400' },
        ].map((s, i) => (
          <div key={i} className={`${card} p-4`} data-testid={`analytics-kpi-${i}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${s.color}`}>
              <s.icon className="w-4 h-4" />
            </div>
            <p className="text-xl font-bold text-white">{s.value}</p>
            <p className="text-[11px] text-gray-500 mt-0.5">{s.label}</p>
            {s.sub && <p className="text-[10px] text-gray-600">{s.sub}</p>}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: t('companyAnalytics.statTotalRepairs'), value: rep.total || 0, icon: Wrench },
          { label: t('companyAnalytics.statActive'), value: rep.active || 0, icon: Activity },
          { label: t('companyAnalytics.statCompleted'), value: rep.completed || 0, icon: CheckCircle },
          { label: t('companyAnalytics.statStockItems'), value: stk.total || 0, icon: Package },
          { label: t('companyAnalytics.statOutOfStock'), value: stk.out || 0, icon: AlertTriangle, alert: stk.out > 0 },
        ].map((s, i) => (
          <div key={i} className={`${card} p-3 flex items-center gap-3`}>
            <s.icon className={`w-4 h-4 flex-shrink-0 ${s.alert ? 'text-red-400' : 'text-gray-500'}`} />
            <div>
              <p className={`text-lg font-bold ${s.alert ? 'text-red-400' : 'text-white'}`}>{s.value}</p>
              <p className="text-[10px] text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {rep.by_magasin && (
        <div className={`${card} p-5`} data-testid="analytics-magasin-breakdown">
          <h2 className="text-sm font-semibold text-white mb-4">{t('companyAnalytics.repairsByStore')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-cyan-500/20 bg-cyan-500/5 p-4">
              <div className="text-cyan-300 font-bold mb-2">M1</div>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>{t('companyAnalytics.repairsM1Explicit', { count: rep.by_magasin.M1?.repairs_in_progress_explicit ?? 0 })}</li>
                <li>{t('companyAnalytics.repairsLegacy', { count: rep.by_magasin.M1?.repairs_in_progress_legacy ?? 0 })}</li>
                <li className="text-white font-semibold pt-1">{t('companyAnalytics.totalShown', { count: rep.by_magasin.M1?.repairs_in_progress ?? 0 })}</li>
              </ul>
            </div>
            <div className="rounded-lg border border-purple-500/20 bg-purple-500/5 p-4">
              <div className="text-purple-300 font-bold mb-2">M2</div>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>{t('companyAnalytics.repairsM2', { count: rep.by_magasin.M2?.repairs_in_progress_explicit ?? 0 })}</li>
                <li className="text-white font-semibold pt-1">{t('companyAnalytics.totalShown', { count: rep.by_magasin.M2?.repairs_in_progress ?? 0 })}</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <div className={`${card} p-5`}>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[var(--theme-primary)]" />
            <h2 className="text-sm font-semibold text-white">{t('companyAnalytics.repairsTrend14Days')}</h2>
          </div>
          <div className="flex items-end gap-1 h-32">
            {trend.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className="text-[8px] text-gray-500">{d.count > 0 ? d.count : ''}</span>
                <div
                  className="w-full bg-[var(--theme-primary)] rounded-t-sm transition-all opacity-80"
                  style={{ height: `${Math.max((d.count / maxTrend) * 100, 3)}%`, minHeight: '2px' }}
                />
                <span className="text-[7px] text-gray-600 -rotate-45 origin-center whitespace-nowrap">{d.date}</span>
              </div>
            ))}
          </div>
        </div>

        <div className={`${card} p-5`}>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-amber-400" />
            <h2 className="text-sm font-semibold text-white">{t('companyAnalytics.statusDistribution')}</h2>
          </div>
          {dist.length === 0 ? (
            <p className="text-xs text-gray-600 py-4">{t('companyAnalytics.noRepairs')}</p>
          ) : (
            <div className="space-y-3">
              {dist.map(d => {
                const total = dist.reduce((a, b) => a + b.count, 0) || 1;
                const pct = (d.count / total) * 100;
                const color = STATUS_COLORS[d.status] || '#666';
                const statusLabel = STATUS_LABEL_KEYS[d.status] ? t(STATUS_LABEL_KEYS[d.status]) : d.status;
                return (
                  <div key={d.status}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium" style={{ color }}>{statusLabel}</span>
                      <span className="text-xs text-gray-400">{d.count} ({pct.toFixed(0)}%)</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className={`${card} p-5`}>
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-4 h-4 text-emerald-400" />
          <h2 className="text-sm font-semibold text-white">{t('companyAnalytics.revenue')}</h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: t('companyAnalytics.revToday'), value: rev.today || 0 },
            { label: t('companyAnalytics.revThisWeek'), value: rev.week || 0 },
            { label: t('companyAnalytics.revThisMonth'), value: rev.month || 0 },
            { label: t('companyAnalytics.revTotal'), value: rev.total || 0 },
          ].map((r, i) => (
            <div key={i} className="bg-white/[0.02] rounded-lg p-3 text-center border border-white/5">
              <p className="text-lg font-bold text-emerald-400">{eur0(r.value)}</p>
              <p className="text-[10px] text-gray-500">{r.label}</p>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default CompanyAnalyticsPage;
